import React, { useContext, useEffect, useState } from "react";
import HeadingBanner from "../Components/HeadingBanner.jsx";
import Dashboard from "../Components/Dashboard.jsx";
import { Logindetail } from "../context/Logindetail.js";
import { getAvailableStockApi, getStockHistoryApi, getOutOfStockApi, getFranchiseDashboardApi, getFranchiseOutApi, getFranchiseProductApi, deductStockApi, getFranchiseOrderListApi, getFranchiseOrderDetailsApi, updateFranchiseDeliveryStatusApi, getProductStockApi, getFranchiseProductListByIdApi } from "../api/Dashboardapi.js";
import { Country_fun, State_fun, City_fun } from "../api/Controllapi.js";
import { Shimmer } from "react-shimmer";
import { FaBuilding, FaIdCard, FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaBoxes, FaHistory, FaExclamationTriangle, FaEye, FaArrowLeft } from "react-icons/fa";
import toast from "react-hot-toast";

const MyFranchise = ({ settings }) => {
  const { detail_info } = useContext(Logindetail);
  const [activeTab, setActiveTab] = useState("details");
  const [availableStock, setAvailableStock] = useState([]);
  const [stockHistory, setStockHistory] = useState([]);
  const [outOfStock, setOutOfStock] = useState([]);
  const [loading, setLoading] = useState(false);
  const [locationNames, setLocationNames] = useState({ country: "", state: "", city: "" });
  const [dashboardData, setDashboardData] = useState(null);
  const [deductStock, setDeductStock] = useState([]);
  const [selectedProductsList, setSelectedProductsList] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [franchiseOrders, setFranchiseOrders] = useState([]);
  const [pendingStatusUpdate, setPendingStatusUpdate] = useState(null);
  const [stockDetailsModal, setStockDetailsModal] = useState({ show: false, loading: false, data: [], productName: "" });
  const [availableStockPage, setAvailableStockPage] = useState(1);
  const [availableStockPagination, setAvailableStockPagination] = useState(null);
  const [stockHistoryPage, setStockHistoryPage] = useState(1);
  const [stockHistoryPagination, setStockHistoryPagination] = useState(null);
  const [outOfStockPage, setOutOfStockPage] = useState(1);
  const [outOfStockPagination, setOutOfStockPagination] = useState(null);
  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false);

  useEffect(() => {
    fetchStockData();
  }, [activeTab, availableStockPage, stockHistoryPage, outOfStockPage]);

  // Close dropdown only when the PAGE scrolls, not when scrolling inside the dropdown list
  useEffect(() => {
    const closeOnScroll = (e) => {
      // If the scroll happened inside the dropdown menu, ignore it
      const dropdownMenu = document.querySelector('.dropdown-menu.show');
      if (dropdownMenu && dropdownMenu.contains(e.target)) return;
      // Otherwise close the dropdown (page scroll)
      const openDropdown = document.querySelector('.dropdown-menu.show');
      if (openDropdown) {
        document.body.click();
      }
    };
    window.addEventListener('scroll', closeOnScroll, true);
    return () => window.removeEventListener('scroll', closeOnScroll, true);
  }, []);

  useEffect(() => {
    if (detail_info?.franchise_country || detail_info?.franchise_state || detail_info?.franchise_city) {
      resolveLocationNames();
    }
  }, [detail_info]);

  const resolveLocationNames = async () => {
    try {
      let countryName = "";
      let stateName = "";
      let cityName = "";

      // Resolve Country
      if (detail_info?.franchise_country) {
        const countries = await Country_fun();
        const country = countries?.data?.find(c => String(c.id) === String(detail_info.franchise_country));
        countryName = country?.name || "";
      }

      // Resolve State
      if (detail_info?.franchise_state && detail_info?.franchise_country) {
        const states = await State_fun(detail_info.franchise_country);
        const state = states?.data?.find(s => String(s.id) === String(detail_info.franchise_state));
        stateName = state?.state || "";
      }

      // Resolve City
      if (detail_info?.franchise_city && detail_info?.franchise_country && detail_info?.franchise_state) {
        const cities = await City_fun(detail_info.franchise_country, detail_info.franchise_state);
        const city = cities?.data?.find(c => String(c.id) === String(detail_info.franchise_city));
        cityName = city?.city || "";
      }

      setLocationNames({
        country: countryName,
        state: stateName,
        city: cityName
      });
    } catch (error) {
      console.error("Error resolving location names:", error);
    }
  };

  const fetchStockData = async () => {
    setLoading(true);
    try {
      if (activeTab === "available") {
        const res = await getAvailableStockApi(availableStockPage);
        setAvailableStock(res?.data || []);
        setAvailableStockPagination(res?.pagination || null);
      } else if (activeTab === "history") {
        const res = await getStockHistoryApi(stockHistoryPage);
        setStockHistory(res?.data || []);
        setStockHistoryPagination(res?.pagination || null);
      } else if (activeTab === "outofstock") {
        const res = await getOutOfStockApi(outOfStockPage);
        setOutOfStock(res?.data || []);
        setOutOfStockPagination(res?.pagination || null);
      } else if (activeTab === "deduct-stock") {
        const res = await getFranchiseProductApi();
        setDeductStock(res?.data || []);
      } else if (activeTab === "details" && detail_info?.id) {
        const res = await getFranchiseDashboardApi(detail_info.id);
        setDashboardData(res?.data || null);

        // Also fetch lists in background if needed for counts, 
        // but the dashboard API already provides the main counts.
        // For consistency with existing state:
        if (res?.data?.stock) {
          // We can keep these for the list views
        }
      } else if (activeTab === "order") {
        const res = await getFranchiseOrderListApi();
        setFranchiseOrders(res?.data || []);
      }
    } catch (error) {
      console.error("Error fetching stock data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewOrder = async (item) => {
    setLoading(true);
    try {
      const res = await getFranchiseOrderDetailsApi(item.id);
      if (res?.order) {
        setSelectedOrder(res.order);
      } else {
        toast.error("Failed to fetch order details.");
      }
    } catch (error) {
      console.error("Error fetching order details:", error);
      toast.error("Error fetching order details.");
    } finally {
      setLoading(false);
    }
  };

  const handleViewProductStockDetails = async (item) => {
    setStockDetailsModal({ show: true, loading: true, data: [], productName: item.product || item.name || "Product" });
    try {
      const pId = item.product_id || item.id;
      const res = await getFranchiseProductListByIdApi(pId);
      const historyList = res?.data?.grams || [];
      setStockDetailsModal(prev => ({ ...prev, loading: false, data: Array.isArray(historyList) ? historyList : [] }));
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch stock details");
      setStockDetailsModal(prev => ({ ...prev, loading: false }));
    }
  };

  const confirmDeliveryStatusUpdate = async () => {
    if (!pendingStatusUpdate) return;
    const { orderId, newStatus } = pendingStatusUpdate;
    setPendingStatusUpdate(null);
    try {
      setLoading(true);
      const res = await updateFranchiseDeliveryStatusApi(orderId, newStatus);
      if (res?.status === 200) {
        toast.success("Order status updated successfully");
        setSelectedOrder({ ...selectedOrder, delivery_status: newStatus });
        fetchStockData();
      } else {
        toast.error(res?.message || "Failed to update status");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error updating status");
    } finally {
      setLoading(false);
    }
  };

  const handleDeductForm = async () => {
    if (selectedProductsList.length === 0) {
      toast.error("Please select at least one product.");
      return;
    }

    // Validate quantities
    for (let item of selectedProductsList) {
      const qty = parseInt(item.quantity || 0);
      if (item.product.unit_id != 6 && (!qty || qty <= 0 || qty > item.product.quantity)) {
        toast.error(`Please enter a valid quantity for ${item.product.name || item.product.product}`);
        return;
      }
      if (item.product.unit_id == 6 && item.selectedGramSlots.length === 0) {
        toast.error(`Please select at least one gram slot for ${item.product.name || item.product.product}`);
        return;
      }
    }

    try {
      const formData = new FormData();
      let pIndex = 0;

      for (let item of selectedProductsList) {
        const pId = item.product.id;
        const qty = parseInt(item.quantity || 0);
        const finalQty = item.product.unit_id == 6 ? item.selectedGramSlots.length : qty;

        formData.append(`product_id[${pIndex}]`, pId);
        formData.append(`quantity[${pId}]`, finalQty);

        if (item.product.unit_id == 6 && item.selectedGramSlots.length > 0) {
          item.selectedGramSlots.forEach((gramId) => {
            formData.append(`gram_qty[${pId}][${gramId}]`, 1);
          });
        }

        pIndex++;
      }

      await deductStockApi(formData);

      toast.success("Stock deducted successfully.");
      setSelectedProductsList([]);
      // Refresh the data
      fetchStockData();
    } catch (error) {
      console.error("Error deducting stock:", error);
      let errMsg = error?.response?.data?.message || "Failed to deduct stock.";
      if (error?.response?.data?.errors) {
        errMsg += "\n" + Object.values(error.response.data.errors).map(e => e.join(", ")).join("\n");
      }
      toast.error(errMsg);
    }
  };

  return (
    <>
      <HeadingBanner title={"My Franchise"} />
      <section className="mb-5">
        <div className="container">
          <div className="profile_layout">
            <div className="row">
              <div className="col-xl-3 col-lg-4">
                <Dashboard setting_data={settings} />
              </div>

              <div className="col-xl-9 col-lg-8">

                {/* Stock Management Section */}
                <div className="stock-management mt-5">
                  <div className="bd-heads pb-3 d-flex justify-content-between align-items-center">
                    <h4>Franchise Details</h4>
                  </div>

                  {/* Tab Switcher */}
                  <div className="d-flex gap-2 mb-4 border-bottom pb-2 flex-wrap">
                    <button
                      className={`btn ${activeTab === 'details' ? 'btn-dark' : 'btn-outline-dark'}`}
                      style={{ borderRadius: '8px 8px 0 0', transition: 'all 0.3s' }}
                      onClick={() => setActiveTab('details')}
                    >
                      Overview
                    </button>
                    <button
                      className={`btn ${activeTab === 'available' ? 'btn-dark' : 'btn-outline-dark'}`}
                      style={{ borderRadius: '8px 8px 0 0', transition: 'all 0.3s' }}
                      onClick={() => setActiveTab('available')}
                    >
                      Available Stock
                    </button>
                    <button
                      className={`btn ${activeTab === 'history' ? 'btn-dark' : 'btn-outline-dark'}`}
                      style={{ borderRadius: '8px 8px 0 0', transition: 'all 0.3s' }}
                      onClick={() => setActiveTab('history')}
                    >
                      Stock History
                    </button>
                    <button
                      className={`btn ${activeTab === 'outofstock' ? 'btn-dark' : 'btn-outline-dark'}`}
                      style={{ borderRadius: '8px 8px 0 0', transition: 'all 0.3s' }}
                      onClick={() => setActiveTab('outofstock')}
                    >
                      Sold Out Stock
                    </button>
                    <button
                      className={`btn ${activeTab === 'deduct-stock' ? 'btn-dark' : 'btn-outline-dark'}`}
                      style={{ borderRadius: '8px 8px 0 0', transition: 'all 0.3s' }}
                      onClick={() => setActiveTab('deduct-stock')}
                    >
                      Deduct Stock
                    </button>
                    <button
                      className={`btn ${activeTab === 'order' ? 'btn-dark' : 'btn-outline-dark'}`}
                      style={{ borderRadius: '8px 8px 0 0', transition: 'all 0.3s' }}
                      onClick={() => setActiveTab('order')}
                    >
                      Orders
                    </button>
                  </div>

                  {/* Tables Content */}
                  <div className={`${activeTab === 'deduct-stock' ? '' : 'table-responsive'} bg-white p-3 border rounded shadow-sm`}>
                    {loading ? (
                      <div className="py-5 text-center">
                        <Shimmer height={200} width={800} />
                      </div>
                    ) : (
                      <>
                        {activeTab === 'details' && (
                          <div className="myprofile-content mb-3">
                            <div className="row g-4 mb-5">
                              {/* Available Stock Card */}
                              <div className="col-lg-3 col-md-6">
                                <div
                                  className="card border-0 shadow-sm rounded-4 p-4 text-center h-100 stock-card"
                                  style={{ cursor: 'pointer', transition: 'all 0.3s', backgroundColor: '#eef2ff' }}
                                  onClick={() => setActiveTab('available')}
                                >
                                  <div className="mx-auto mb-3 p-3 rounded-circle bg-white shadow-sm text-primary" style={{ width: 'fit-content' }}>
                                    <FaBoxes size={30} />
                                  </div>
                                  <h3 className="fw-bold mb-1">{dashboardData?.stock?.available_stock || 0}</h3>
                                  <p className="text-muted mb-0 fw-semibold">Available Stock</p>
                                </div>
                              </div>

                              {/* Total Stock Card */}
                              <div className="col-lg-3 col-md-6">
                                <div
                                  className="card border-0 shadow-sm rounded-4 p-4 text-center h-100 stock-card"
                                  style={{ cursor: 'pointer', transition: 'all 0.3s', backgroundColor: '#f0fdf4' }}
                                  onClick={() => setActiveTab('history')}
                                >
                                  <div className="mx-auto mb-3 p-3 rounded-circle bg-white shadow-sm text-success" style={{ width: 'fit-content' }}>
                                    <FaHistory size={30} />
                                  </div>
                                  <h3 className="fw-bold mb-1">{dashboardData?.stock?.total_stock || 0}</h3>
                                  <p className="text-muted mb-0 fw-semibold">Total Stock</p>
                                </div>
                              </div>

                              {/* Sold Stock Card */}
                              <div className="col-lg-3 col-md-6">
                                <div
                                  className="card border-0 shadow-sm rounded-4 p-4 text-center h-100 stock-card"
                                  style={{ cursor: 'pointer', transition: 'all 0.3s', backgroundColor: '#fef2f2' }}
                                  onClick={() => setActiveTab('outofstock')}
                                >
                                  <div className="mx-auto mb-3 p-3 rounded-circle bg-white shadow-sm text-danger" style={{ width: 'fit-content' }}>
                                    <FaExclamationTriangle size={30} />
                                  </div>
                                  <h3 className="fw-bold mb-1">{dashboardData?.stock?.sold_stock || 0}</h3>
                                  <p className="text-muted mb-0 fw-semibold">Sold Stock</p>
                                </div>
                              </div>

                              {/* Today Added Card */}
                              <div className="col-lg-3 col-md-6">
                                <div
                                  className="card border-0 shadow-sm rounded-4 p-4 text-center h-100 stock-card"
                                  style={{ cursor: 'pointer', transition: 'all 0.3s', backgroundColor: '#fff7ed' }}
                                  onClick={() => setActiveTab('history')}
                                >
                                  <div className="mx-auto mb-3 p-3 rounded-circle bg-white shadow-sm text-warning" style={{ width: 'fit-content' }}>
                                    <FaBoxes size={30} />
                                  </div>
                                  <h3 className="fw-bold mb-1">{dashboardData?.stock?.today_added_stock || 0}</h3>
                                  <p className="text-muted mb-0 fw-semibold">Today Added</p>
                                </div>
                              </div>
                            </div>

                            <style>
                              {`
                                .stock-card:hover {
                                  transform: translateY(-8px);
                                  box-shadow: 0 1rem 3rem rgba(0,0,0,.175) !important;
                                }
                              `}
                            </style>

                            <div className="bd-heads pb-3">
                              <h4 className="fw-bold">Franchise Profile</h4>
                            </div>
                            <div className="bd-form border-0 p-4 bg-white rounded-4 shadow-sm" style={{ border: '1px solid #f0f0f0' }}>
                              <div className="row g-4">
                                {/* Left Column */}
                                <div className="col-md-6">
                                  <div className="d-flex align-items-start gap-3 mb-4">
                                    <div className="p-2 rounded-3 bg-light text-primary">
                                      <FaBuilding size={20} />
                                    </div>
                                    <div>
                                      <label className="fw-bold text-muted small text-uppercase d-block mb-1">Company Name</label>
                                      <p className="fs-6 fw-semibold mb-0 text-dark">{dashboardData?.franchise?.franchise_name || detail_info?.franchise_name || "N/A"}</p>
                                    </div>
                                  </div>

                                  <div className="d-flex align-items-start gap-3 mb-4">
                                    <div className="p-2 rounded-3 bg-light text-success">
                                      <FaIdCard size={20} />
                                    </div>
                                    <div>
                                      <label className="fw-bold text-muted small text-uppercase d-block mb-1">GST Number</label>
                                      <p className="fs-6 fw-semibold mb-0 text-dark">{dashboardData?.franchise?.franchise_gst || detail_info?.franchise_gst || "N/A"}</p>
                                    </div>
                                  </div>

                                  <div className="d-flex align-items-start gap-3 mb-0">
                                    <div className="p-2 rounded-3 bg-light text-warning">
                                      <FaPhoneAlt size={20} />
                                    </div>
                                    <div>
                                      <label className="fw-bold text-muted small text-uppercase d-block mb-1">Phone Number</label>
                                      <p className="fs-6 fw-semibold mb-0 text-dark">{dashboardData?.franchise?.franchise_phone || detail_info?.franchise_phone || "N/A"}</p>
                                    </div>
                                  </div>
                                </div>

                                {/* Right Column */}
                                <div className="col-md-6">
                                  <div className="d-flex align-items-start gap-3 mb-4">
                                    <div className="p-2 rounded-3 bg-light text-danger">
                                      <FaEnvelope size={20} />
                                    </div>
                                    <div>
                                      <label className="fw-bold text-muted small text-uppercase d-block mb-1">Email Address</label>
                                      <p className="fs-6 fw-semibold mb-0 text-dark text-break">{dashboardData?.franchise?.email || detail_info?.email || "N/A"}</p>
                                    </div>
                                  </div>

                                  <div className="d-flex align-items-start gap-3 mb-0">
                                    <div className="p-2 rounded-3 bg-light text-info">
                                      <FaMapMarkerAlt size={20} />
                                    </div>
                                    <div className="w-100">
                                      <label className="fw-bold text-muted small text-uppercase d-block mb-1">Registered Address</label>
                                      <p className="fs-6 fw-semibold mb-0 text-dark lh-sm">
                                        {dashboardData?.franchise?.franchise_address || detail_info?.franchise_address}<br />
                                        <span className="text-muted small">
                                          {dashboardData?.franchise?.franchise_city || locationNames.city || detail_info?.franchise_city}, {dashboardData?.franchise?.franchise_state || locationNames.state || detail_info?.franchise_state}, {dashboardData?.franchise?.franchise_country || locationNames.country || detail_info?.franchise_country} - {dashboardData?.franchise?.franchise_postal || detail_info?.franchise_postal}
                                        </span>
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        {activeTab === 'available' && (
                          <div className="bd-heads pb-3">
                            <h4 className="fw-bold">Available Stock</h4>
                          </div>
                        )}
                        {activeTab === 'history' && (
                          <div className="bd-heads pb-3">
                            <h4 className="fw-bold">Stock History</h4>
                          </div>
                        )}
                        {activeTab === 'outofstock' && (
                          <div className="bd-heads pb-3">
                            <h4 className="fw-bold">Out Of Stock</h4>
                          </div>
                        )}
                        {activeTab === 'deduct-stock' && (
                          <div className="bd-heads pb-3">
                            <h4 className="fw-bold">Deduct Stock</h4>
                          </div>
                        )}
                        {activeTab === 'order' && (
                          <div className="bd-heads pb-3">
                            <h4 className="fw-bold">Franchise Orders</h4>
                          </div>
                        )}
                        {activeTab !== 'details' && activeTab !== 'deduct-stock' && activeTab !== 'order' && (
                          <>
                            <table className="table table-hover align-middle">
                              <thead className="table-light">
                                {activeTab === 'available' && (
                                  <tr>

                                    <th>S.No</th>
                                    <th>Product Name</th>
                                    <th>Quantity</th>
                                    <th>Last Updated</th>
                                  </tr>
                                )}
                                {activeTab === 'history' && (
                                  <tr>
                                    <th>S.No</th>
                                    <th>Product Name</th>
                                    <th>Type</th>
                                    <th>Quantity</th>
                                    <th>Date</th>
                                  </tr>
                                )}
                                {activeTab === 'outofstock' && (
                                  <tr>
                                    <th>S.No</th>
                                    <th>Product Name</th>
                                    <th>Quantity</th>
                                    <th>Date</th>
                                  </tr>
                                )}
                              </thead>
                              <tbody>
                                {activeTab === 'available' && (
                                  availableStock.length > 0 ? availableStock.map((item, index) => (
                                    <tr key={index}>
                                      <td>{index + 1}</td>
                                      {item.type === 'gram' ? (
                                        <td className="fw-bold text-primary" style={{ cursor: "pointer", textDecoration: "none" }} onClick={() => handleViewProductStockDetails(item)}>
                                          {item.product}
                                        </td>
                                      ) : (
                                        <td className="fw-bold text-dark">
                                          {item.product}
                                        </td>
                                      )}
                                      <td><span className="badge bg-success">{item.quantity}</span></td>
                                      <td>{item.date}</td>
                                    </tr>
                                  )) : <tr><td colSpan="4" className="text-center py-4 text-muted">No available stock found.</td></tr>
                                )}
                                {activeTab === 'history' && (
                                  stockHistory.length > 0 ? stockHistory.map((item, index) => (
                                    <tr key={index}>
                                      <td>{index + 1}</td>
                                      <td className="fw-bold">{item.product}</td>
                                      <td>
                                        <span className={`badge ${item.type === 'IN' ? 'bg-success' : 'bg-danger'}`}>
                                          {item.type}
                                        </span>
                                      </td>
                                      <td>{item.quantity}</td>
                                      <td>{item.date}</td>
                                    </tr>
                                  )) : <tr><td colSpan="5" className="text-center py-4 text-muted">No stock history record found.</td></tr>
                                )}
                                {activeTab === 'history' && stockHistoryPagination && stockHistoryPagination.last_page > 1 && (
                                  <tr className="border-0">
                                    <td colSpan="5" className="border-0 p-0 text-center">
                                      <div className="d-flex justify-content-center mt-3 mb-1">
                                        <nav>
                                          <ul className="pagination pagination-sm m-0">
                                            <li className={`page-item ${stockHistoryPage === 1 ? 'disabled' : ''}`}>
                                              <button className="page-link" onClick={() => setStockHistoryPage(prev => Math.max(1, prev - 1))}>Previous</button>
                                            </li>
                                            {[...Array(stockHistoryPagination.last_page)].map((_, idx) => (
                                              <li key={idx} className={`page-item ${stockHistoryPage === idx + 1 ? 'active' : ''}`}>
                                                <button className="page-link" onClick={() => setStockHistoryPage(idx + 1)}>{idx + 1}</button>
                                              </li>
                                            ))}
                                            <li className={`page-item ${stockHistoryPage === stockHistoryPagination.last_page ? 'disabled' : ''}`}>
                                              <button className="page-link" onClick={() => setStockHistoryPage(prev => Math.min(stockHistoryPagination.last_page, prev + 1))}>Next</button>
                                            </li>
                                          </ul>
                                        </nav>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                                {activeTab === 'outofstock' && (
                                  outOfStock.length > 0 ? outOfStock.map((item, index) => (
                                    <tr key={index}>
                                      <td>{index + 1}</td>
                                      <td className="fw-bold text-dark">
                                        {item.product} {item.garm && <span className="text-danger fw-normal ms-1">({item.garm}g)</span>}
                                      </td>
                                      <td><span className="badge bg-danger">{item.quantity || 0}</span></td>
                                      <td>{item.date}</td>
                                    </tr>
                                  )) : <tr><td colSpan="4" className="text-center py-4 text-muted">No items currently out of stock.</td></tr>
                                )}
                                {activeTab === 'outofstock' && outOfStockPagination && outOfStockPagination.last_page > 1 && (
                                  <tr className="border-0">
                                    <td colSpan="4" className="border-0 p-0 text-center">
                                      <div className="d-flex justify-content-center mt-3 mb-1">
                                        <nav>
                                          <ul className="pagination pagination-sm m-0">
                                            <li className={`page-item ${outOfStockPage === 1 ? 'disabled' : ''}`}>
                                              <button className="page-link" onClick={() => setOutOfStockPage(prev => Math.max(1, prev - 1))}>Previous</button>
                                            </li>
                                            {[...Array(outOfStockPagination.last_page)].map((_, idx) => (
                                              <li key={idx} className={`page-item ${outOfStockPage === idx + 1 ? 'active' : ''}`}>
                                                <button className="page-link" onClick={() => setOutOfStockPage(idx + 1)}>{idx + 1}</button>
                                              </li>
                                            ))}
                                            <li className={`page-item ${outOfStockPage === outOfStockPagination.last_page ? 'disabled' : ''}`}>
                                              <button className="page-link" onClick={() => setOutOfStockPage(prev => Math.min(outOfStockPagination.last_page, prev + 1))}>Next</button>
                                            </li>
                                          </ul>
                                        </nav>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                            {activeTab === 'available' && availableStockPagination && availableStockPagination.last_page > 1 && (
                              <div className="d-flex justify-content-center mt-3">
                                <nav>
                                  <ul className="pagination pagination-sm">
                                    <li className={`page-item ${availableStockPage === 1 ? 'disabled' : ''}`}>
                                      <button className="page-link" onClick={() => setAvailableStockPage(prev => Math.max(1, prev - 1))}>Previous</button>
                                    </li>
                                    {[...Array(availableStockPagination.last_page)].map((_, idx) => (
                                      <li key={idx} className={`page-item ${availableStockPage === idx + 1 ? 'active' : ''}`}>
                                        <button className="page-link" onClick={() => setAvailableStockPage(idx + 1)}>{idx + 1}</button>
                                      </li>
                                    ))}
                                    <li className={`page-item ${availableStockPage === availableStockPagination.last_page ? 'disabled' : ''}`}>
                                      <button className="page-link" onClick={() => setAvailableStockPage(prev => Math.min(availableStockPagination.last_page, prev + 1))}>Next</button>
                                    </li>
                                  </ul>
                                </nav>
                              </div>
                            )}
                          </>
                        )}
                        {activeTab === 'deduct-stock' && (
                          <div className="deduct-form">
                            {/* <h5 className="mb-4">Deduct Stock</h5> */}
                            <div className="mb-3">
                              <label htmlFor="product-select" className="form-label">Select Product</label>
                              <div className="dropdown w-100">
                                <button className="form-select text-start w-100" type="button" data-bs-toggle="dropdown" aria-expanded="false" style={{ maxWidth: "100%" }}>
                                  Type to search products...
                                </button>
                                <ul className="dropdown-menu w-100 shadow" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                  {deductStock.map((item) => {
                                    const isAlreadySelected = !!selectedProductsList.find(pi => pi.product.id === item.id);
                                    return (
                                      <li key={item.id}>
                                        <button
                                          className="dropdown-item py-2 border-bottom"
                                          disabled={isAlreadySelected}
                                          style={isAlreadySelected ? { opacity: 0.5, cursor: 'not-allowed', backgroundColor: '#f8f9fa' } : {}}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            if (!isAlreadySelected) {
                                              setSelectedProductsList([
                                                ...selectedProductsList,
                                                {
                                                  product: item,
                                                  selectedGramSlots: [],
                                                  quantity: item.unit_id == 6 ? 0 : ""
                                                }
                                              ]);
                                            }
                                          }}
                                        >
                                          <div className="d-flex justify-content-between align-items-center">
                                            <div className="fw-bold fs-6 text-wrap">{item.name || item.product}</div>
                                            {isAlreadySelected && <span className="badge bg-success ms-2" style={{ fontSize: '11px' }}>✓ Added</span>}
                                          </div>
                                          <div className="small text-muted mt-1">Stock: {item.quantity} {item.unit_id == 6 ? <span className="text-secondary">(Has Gram Slots)</span> : ""}</div>
                                        </button>
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            </div>

                            {selectedProductsList.map((selectedItem, idx) => {
                              const p = selectedItem.product;
                              return (
                                <div key={p.id} className="mb-4 border rounded p-3 bg-white position-relative shadow-sm">
                                  <button
                                    className="btn btn-sm btn-outline-danger position-absolute"
                                    style={{ top: "10px", right: "10px" }}
                                    onClick={() => setSelectedProductsList(selectedProductsList.filter(item => item.product.id !== p.id))}
                                  >
                                    &times;
                                  </button>
                                  <h6 className="mb-3 fw-bold pe-4">
                                    {p.name || p.product} <span className="text-muted fw-normal">(Stock: {p.quantity})</span> {p.unit_id == 6 ? <span className="text-secondary fw-normal">(Has Gram Slots)</span> : ""}
                                  </h6>

                                  {p.unit_id == 6 && p.grams && p.grams.length > 0 && (
                                    <div className="mb-3">
                                      <label className="form-label text-primary d-flex align-items-center gap-2">
                                        Select Gram Slots
                                      </label>
                                      <div className="d-flex flex-wrap gap-2">
                                        {p.grams.map((gram) => (
                                          <div key={gram.id} className="form-check border rounded p-2 px-3 d-flex align-items-center gap-2 m-0 bg-white" style={{ flex: "1 1 auto" }}>
                                            <input
                                              className="form-check-input mt-0"
                                              type="checkbox"
                                              id={`gram-${p.id}-${gram.id}`}
                                              value={gram.id}
                                              checked={selectedItem.selectedGramSlots.includes(gram.id)}
                                              onChange={(e) => {
                                                const newSlots = e.target.checked
                                                  ? [...selectedItem.selectedGramSlots, gram.id]
                                                  : selectedItem.selectedGramSlots.filter(id => id !== gram.id);

                                                const newList = [...selectedProductsList];
                                                newList[idx].selectedGramSlots = newSlots;
                                                newList[idx].quantity = newSlots.length;
                                                setSelectedProductsList(newList);
                                              }}
                                            />
                                            <label className="form-check-label mb-0 text-dark w-100" htmlFor={`gram-${p.id}-${gram.id}`} style={{ cursor: "pointer", fontSize: "14px" }}>
                                              {gram.slot_label}
                                            </label>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  <div className="mb-0">
                                    <label htmlFor={`deduct-quantity-${p.id}`} className="form-label">
                                      {p.unit_id == 6 ? "Total Selected Qty" : "Quantity to Deduct"}
                                    </label>
                                    <input
                                      type="number"
                                      className="form-control"
                                      id={`deduct-quantity-${p.id}`}
                                      min="1"
                                      max={p.quantity || 0}
                                      placeholder="Enter quantity"
                                      disabled={p.unit_id == 6}
                                      style={{ backgroundColor: p.unit_id == 6 ? "#e9ecef" : "" }}
                                      value={selectedItem.quantity}
                                      onChange={(e) => {
                                        const newList = [...selectedProductsList];
                                        newList[idx].quantity = e.target.value;
                                        setSelectedProductsList(newList);
                                      }}
                                    />
                                  </div>
                                </div>
                              );
                            })}

                            <button
                              className="btn btn-danger mt-3"
                              onClick={handleDeductForm}
                              disabled={selectedProductsList.length === 0}
                            >
                              Deduct
                            </button>
                          </div>
                        )}
                        {activeTab === 'order' && (
                          <div className="order-form">
                            {!selectedOrder ? (
                              <>
                                <div className="mb-3 d-flex justify-content-between align-items-center">
                                  <h6 className="mb-0 text-muted">Order List</h6>
                                </div>
                                <div className="table-responsive">
                                  <table className="table table-hover align-middle border" style={{ fontSize: "14px" }}>
                                    <thead className="table-light">
                                      <tr>
                                        <th>#</th>
                                        <th>Order ID</th>
                                        <th>Customer Name</th>
                                        <th>Customer ID</th>
                                        <th>No. of Products</th>
                                        <th>Grand Total</th>
                                        <th>Delivery Status</th>
                                        <th>Action</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {franchiseOrders.length > 0 ? franchiseOrders.map((item, idx) => (
                                        <tr key={idx}>
                                          <td>{idx + 1}</td>
                                          <td><a href="#" className="text-primary text-decoration-none fw-semibold">{item.code}</a></td>
                                          <td>{item.user?.name}</td>
                                          <td>{item.user?.code}</td>
                                          <td>{item.order_items?.length || 0}</td>
                                          <td>{Number(item.grand_total || 0).toFixed(2)}</td>
                                          <td><span className="badge bg-light text-dark border px-2 py-1 text-capitalize">{item.delivery_status}</span></td>
                                          <td>
                                            <button className="btn btn-sm text-secondary p-1" title="View Order" onClick={() => handleViewOrder(item)}>
                                              <FaEye size={18} />
                                            </button>
                                          </td>
                                        </tr>
                                      )) : (
                                        <tr><td colSpan="8" className="text-center py-4 text-muted">No orders found.</td></tr>
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </>
                            ) : (
                              <div className="order-details border rounded p-4 bg-white shadow-sm">
                                <div className="d-flex align-items-center mb-4 pb-3 border-bottom">
                                  <button className="btn btn-outline-secondary btn-sm me-3" onClick={() => setSelectedOrder(null)}>
                                    <FaArrowLeft /> Back
                                  </button>
                                  <h5 className="mb-0 fw-bold">Order Details</h5>
                                </div>

                                <div className="row g-4 mb-4">
                                  <div className="col-md-6">
                                    <label className="form-label text-muted small">Payment Status</label>
                                    <input type="text" className="form-control bg-light text-capitalize" value={selectedOrder.payment_status || "N/A"} readOnly />
                                  </div>
                                  <div className="col-md-6">
                                    <label className="form-label text-muted small">Delivery Status</label>
                                    <select
                                      className="form-select bg-light text-capitalize"
                                      value={selectedOrder.delivery_status || "pending"}
                                      onChange={(e) => setPendingStatusUpdate({ orderId: selectedOrder.id, newStatus: e.target.value })}
                                      disabled={selectedOrder.delivery_status === 'delivered' || selectedOrder.delivery_status === 'cancelled'}
                                    >
                                      <option value="pending">Pending</option>
                                      <option value="confirmed">Confirmed</option>
                                      <option value="picked_up">Picked Up</option>
                                      <option value="delivered">Delivered</option>
                                    </select>
                                  </div>
                                </div>

                                <div className="row bg-light p-3 rounded mb-4 mx-0">
                                  <div className="col-md-6 border-end text-center d-flex flex-column justify-content-center">
                                    <h6 className="fw-bold mb-2">{selectedOrder.shipping_address?.name || selectedOrder.user?.name || "N/A"}</h6>
                                    <p className="text-muted mb-0 small text-break">
                                      {(() => {
                                        try {
                                          const addr = typeof selectedOrder.shipping_address === 'string' ? JSON.parse(selectedOrder.shipping_address) : selectedOrder.shipping_address;
                                          return `${addr?.address || ''}, ${addr?.city || ''}, ${addr?.state || ''}, ${addr?.country || ''} - ${addr?.postal_code || ''}`;
                                        } catch (e) {
                                          return selectedOrder.shipping_address || "";
                                        }
                                      })()}
                                    </p>
                                  </div>
                                  <div className="col-md-6 ps-4">
                                    <div className="d-flex justify-content-between mb-1 small">
                                      <span className="text-muted">Order #</span>
                                      <span className="text-primary fw-semibold">{selectedOrder.order_number || selectedOrder.code}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-1 small align-items-center">
                                      <span className="text-muted">Order Status</span>
                                      <span className="badge bg-primary text-capitalize">{selectedOrder.delivery_status}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-1 small">
                                      <span className="text-muted">Order Date</span>
                                      <span className="text-dark">{new Date(selectedOrder.created_at).toLocaleString()}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-1 small">
                                      <span className="text-muted">Total amount</span>
                                      <span className="fw-bold">{Number(selectedOrder.grand_total || 0).toFixed(2)}</span>
                                    </div>
                                  </div>
                                </div>

                                <div className="table-responsive mb-4">
                                  <table className="table table-bordered border-top" style={{ fontSize: "13px" }}>
                                    <thead className="table-light text-muted">
                                      <tr>
                                        <th>#</th>
                                        <th>Photo</th>
                                        <th>DESCRIPTION</th>
                                        <th>PRODUCT BY</th>
                                        <th>QTY</th>
                                        <th className="text-end">TOTAL</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(selectedOrder.items || selectedOrder.order_items)?.map((oItem, i) => (
                                        <tr key={i}>
                                          <td>{i + 1}</td>
                                          <td>
                                            {oItem.product_image ? (
                                              <img src={oItem.product_image} alt="product" style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: "4px" }} />
                                            ) : (
                                              <div style={{ width: "40px", height: "40px", backgroundColor: "#0b2046", borderRadius: "4px", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px" }}>Item</div>
                                            )}
                                          </td>
                                          <td>
                                            <div className="fw-semibold text-primary">{oItem.product_name || oItem.product_details?.name || `Product #${oItem.id}`}</div>
                                            <div className="text-muted small">Price Charges: {Number(oItem.charges?.price || oItem.price || 0).toFixed(2)}</div>
                                            <div className="text-muted small">Making Charges: {Number(oItem.charges?.making_charge || oItem.markting || 0).toFixed(2)}</div>
                                            <div className="text-muted small">Wastage Charges: {Number(oItem.charges?.wastage_charge || oItem.wastage || 0).toFixed(2)}</div>
                                            <div className="text-muted small">Other Charges: {Number(oItem.charges?.other_charge || oItem.other || 0).toFixed(2)}</div>
                                            {Number(oItem.gram) > 0 && (
                                              <div className="text-muted small">Gram: {oItem.gram}g</div>
                                            )}
                                          </td>
                                          <td>{oItem.vendor_type || "-"}</td>
                                          <td>{oItem.qty}</td>
                                          <td className="text-end text-dark">{oItem.sub_total || oItem.grand_total || "0.00"}</td>
                                        </tr>
                                      )) || <tr><td colSpan="6" className="text-center">No items found</td></tr>}
                                    </tbody>
                                  </table>
                                </div>

                                <div className="row justify-content-end">
                                  <div className="col-md-5">
                                    <div className="d-flex justify-content-between mb-2 small">
                                      <span className="text-muted">Sub Total :</span>
                                      <span>{(selectedOrder.grand_total - selectedOrder.shipping_cost - selectedOrder.tax).toFixed(2)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-2 small">
                                      <span className="text-muted">Tax :</span>
                                      <span>{Number(selectedOrder.tax || 0).toFixed(2)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-2 small">
                                      <span className="text-muted">Shipping :</span>
                                      <span>{Number(selectedOrder.shipping_cost || 0).toFixed(2)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-3 small">
                                      <span className="text-muted">Discount :</span>
                                      <span>{Number(selectedOrder.coupon_discount || 0).toFixed(2)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between fw-bold pt-2 border-top">
                                      <span className="text-muted">TOTAL :</span>
                                      <span className="text-primary">{Number(selectedOrder.grand_total || 0).toFixed(2)}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Confirmation Modal */}
      {pendingStatusUpdate && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1040, backgroundColor: 'rgba(0,0,0,0.5)' }}></div>
          <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow">
                <div className="modal-body text-center p-5">
                  <div className="mb-4 text-dark">
                    <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" fill="currentColor" className="bi bi-exclamation-circle" viewBox="0 0 16 16">
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                      <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z" />
                    </svg>
                  </div>
                  <h5 className="mb-4 fw-bold">Are you sure you want to update the delivery status?</h5>
                  <div className="d-flex justify-content-center gap-3">
                    <button type="button" className="btn btn-danger px-4" style={{ backgroundColor: '#b30000', borderColor: '#b30000' }} onClick={confirmDeliveryStatusUpdate}>Yes</button>
                    <button type="button" className="btn btn-primary px-4" onClick={() => setPendingStatusUpdate(null)}>No</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Product Stock Details Modal */}
      {stockDetailsModal.show && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1040, backgroundColor: 'rgba(0,0,0,0.5)' }}></div>
          <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content border-0 shadow">
                <div className="modal-header border-bottom">
                  <h5 className="modal-title fw-bold">Stock Details - {stockDetailsModal.productName}</h5>
                  <button type="button" className="btn-close" onClick={() => setStockDetailsModal({ show: false, loading: false, data: [], productName: "" })}></button>
                </div>
                <div className="modal-body p-4">
                  {stockDetailsModal.loading ? (
                    <div className="text-center py-5">
                      <Shimmer height={100} width={600} className="w-100" />
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-bordered align-middle text-center text-dark" style={{ fontSize: "14px" }}>
                        <thead className="table-light">
                          <tr>
                            <th>S.No</th>
                            <th>Gram</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stockDetailsModal.data.length > 0 ? stockDetailsModal.data.map((record, i) => (
                            <tr key={i}>
                              <td>{i + 1}</td>
                              <td className="fw-bold">{record.slot_label || "N/A"}</td>
                              <td>{record.created_at ? new Date(record.created_at).toLocaleString() : "N/A"}</td>
                            </tr>
                          )) : (
                            <tr><td colSpan="3" className="text-muted py-4">No stock history available for this product.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <div className="modal-footer border-top-0 pt-0">
                  <button type="button" className="btn btn-secondary px-4" onClick={() => setStockDetailsModal({ show: false, loading: false, data: [], productName: "" })}>Close</button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default MyFranchise;
